import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { Assumptions, EconomicsFlag } from '../../../common/models/unit-economics.model';
import { money } from '../costs/cost-format';
import { flagTone } from './ue-format';

/** What a flag's tooltip needs from the rules (the thresholds it was raised by). */
export function flagTip(flag: EconomicsFlag, a: Assumptions | null | undefined): Record<string, string | number> {
  const th = a?.thresholds;
  if (!th) return {};
  switch (flag) {
    case 'trial_cost_high':
      return { usd: money(th.trialCostHighUsd) };
    case 'no_usage_30d':
      return { days: th.noUsageDays };
    case 'renewal_soon':
      return { days: th.renewalSoonDays };
    case 'underpriced':
      return { pct: th.underpricedDiscountPct };
    case 'trial_ending':
      return { days: th.trialEndingDays };
    default:
      return {};
  }
}

/** A customer's flags as small neutral badges; only a loss is red. Each says why on hover. */
export function FlagBadges({ flags, assumptions, className }: { flags: EconomicsFlag[]; assumptions?: Assumptions | null; className?: string }) {
  const { t } = useTranslation();
  if (!flags.length) return <span className="text-muted-foreground">—</span>;
  return (
    <TooltipProvider delayDuration={150}>
      <div className={cn('flex flex-wrap gap-1', className)} data-testid="ue-flags">
        {flags.map((f) => (
          <Tooltip key={f}>
            {/* A span takes the trigger's ref; the badge itself does not forward one. */}
            <TooltipTrigger asChild>
              <span tabIndex={0} className="rounded-4xl focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none">
                <Badge variant={flagTone(f) === 'loss' ? 'destructive' : 'outline'} className="font-normal" data-flag={f}>
                  {t(`UNIT_ECONOMICS.FLAGS.${f}`)}
                </Badge>
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-[260px] leading-relaxed">{t(`UNIT_ECONOMICS.FLAG_TIPS.${f}`, flagTip(f, assumptions))}</TooltipContent>
          </Tooltip>
        ))}
      </div>
    </TooltipProvider>
  );
}
